import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthorization } from '../hooks/use-authorization';
import { useDomains, type CreateDomainInput } from '../state/domain-state';
import { useToast } from '../state/toast-state';
import { Button } from '../components/ui/button';
import { Spinner } from '../components/ui/spinner';
import { EmptyState } from '../components/ui/empty-state';
import { ErrorState } from '../components/ui/error-state';
import { InlineForm, type InlineFormField } from '../components/ui/inline-form';
import { DomainCard } from '../components/domains/DomainCard';

/**
 * Domains Page — Displays domains as a responsive card grid.
 * System_Admin can list all domains and create new ones via InlineForm.
 * Domain_Admin sees only their assigned domains (scoped via API response).
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 6.6, 11.1
 */

const DOMAIN_FORM_FIELDS: readonly InlineFormField[] = [
  { name: 'domainName', label: 'Domain Name', placeholder: 'example.com', required: true },
  { name: 'cloudflareZoneId', label: 'Cloudflare Zone ID', placeholder: 'Cloudflare zone identifier', required: true },
  { name: 'credentialId', label: 'Credential ID', placeholder: 'Associated credential ID', required: true },
];

export function DomainsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { isSystemAdmin } = useAuthorization();
  const { state, loadDomains, createDomain, isConnecting } = useDomains();
  const { showToast } = useToast();
  const [showCreateForm, setShowCreateForm] = React.useState(false);

  React.useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  const handleCardClick = React.useCallback(
    (domainId: string): void => {
      navigate(`/admin-console/domains/${domainId}`);
    },
    [navigate],
  );

  const handleCreateSubmit = React.useCallback(
    async (data: Record<string, string>): Promise<void> => {
      const input: CreateDomainInput = {
        domainName: data['domainName'] ?? '',
        cloudflareZoneId: data['cloudflareZoneId'] ?? '',
        credentialId: data['credentialId'] ?? '',
      };
      await createDomain(input);
      setShowCreateForm(false);
      showToast(`Domain "${input.domainName}" registered successfully.`, 'success');
    },
    [createDomain, showToast],
  );

  const handleCreateCancel = React.useCallback((): void => {
    setShowCreateForm(false);
  }, []);

  const handleShowCreateForm = React.useCallback((): void => {
    setShowCreateForm(true);
  }, []);

  return (
    <section aria-labelledby="domains-heading">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 id="domains-heading" className="text-2xl font-semibold text-foreground">
            Domains
          </h2>
          <p className="text-sm text-muted-foreground">
            {isSystemAdmin ? 'All registered domains.' : 'Domains assigned to you.'}
          </p>
        </div>
        {isSystemAdmin && (
          <Button onClick={handleShowCreateForm} disabled={showCreateForm}>
            Register Domain
          </Button>
        )}
      </div>

      {isSystemAdmin && showCreateForm && (
        <div className="mb-6">
          <InlineForm
            fields={DOMAIN_FORM_FIELDS}
            onSubmit={handleCreateSubmit}
            onCancel={handleCreateCancel}
            submitLabel="Register Domain"
          />
        </div>
      )}

      {isConnecting ? (
        <div className="flex flex-col items-center justify-center gap-3 py-12" aria-busy="true">
          <Spinner label="Connecting..." />
          <p className="text-sm text-muted-foreground">Establishing connection to the server...</p>
        </div>
      ) : state.isLoading ? (
        <Spinner label="Loading domains..." />
      ) : state.error ? (
        <ErrorState message={state.error} onRetry={loadDomains} />
      ) : state.domains.length === 0 ? (
        <EmptyState
          title="No domains registered"
          description="There are no domains registered yet. Register your first domain to get started."
          actionLabel={isSystemAdmin ? 'Register Domain' : undefined}
          onAction={isSystemAdmin ? handleShowCreateForm : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {state.domains.map((domain) => (
            <DomainCard key={domain.domainId} domain={domain} onClick={handleCardClick} />
          ))}
        </div>
      )}
    </section>
  );
}
